import {isEmpty, isNumeric} from '@/lib/utils';
import type {OnChangeFn, PaginationState, SortingState,} from '@tanstack/react-table';
import {useEffect, useMemo, useRef, useState} from 'react';
import {type UseFormReturn, useWatch} from 'react-hook-form';
import { useSearchParams } from '@/hooks/use-search-params.ts';

type PaginationOptions = {
  pageSize?: number;
  params?: any;
  form?: UseFormReturn<any>;
  defPageSize?: number;
};

export interface IPagination {
  pagination: PaginationState;
  params: any;
  sorting: SortingState;
  setSorting: OnChangeFn<SortingState>;
  setPagination: OnChangeFn<PaginationState>;
  reset: () => void;
  // eslint-disable-next-line no-unused-vars
  updateParams: (params: any) => void;
  form?: UseFormReturn<any>;
}

const cleanParams = (dirty: any) => {
  const clean: any = {...dirty};
  Object.keys(clean).forEach((key) => {
    const value: unknown = clean[key];
    if (
      value === undefined ||
      value === '' ||
      (typeof value === 'number' && isNaN(value))
    ) {
      delete clean[key];
    } else if (isNumeric(value)) {
      clean[key] = Number(value);
    }
  });
  return clean;
};

const cleanPageParams = (dirty: any, defPageSize = 15) => {
  const clean: any = cleanParams(dirty);
  Object.keys(clean).forEach((key) => {
    const value: unknown = clean[key];
    if (key === 'pageIndex' && value == 0) {
      delete clean[key];
      return;
    }
    if (key === 'pageSize' && value == defPageSize) {
      delete clean[key];
    }
  });
  return clean;
};

export function usePagination(opts?: PaginationOptions): IPagination {
  const defPageSize = opts?.defPageSize || 15;
  const initParams = useMemo(
    () =>
      cleanParams({pageSize: opts?.pageSize || defPageSize, ...opts?.params}),
    [],
  );
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initParams.pageSize,
  });
  const [params, setParams] = useState<any>(initParams);

  const form = useMemo(() => opts?.form, [opts?.form]);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const formValues = form ? useWatch({control: form.control}) : undefined;
  useEffect(() => {
    if (!formValues || !form) {
      return;
    }
    updatePageParams(formValues);
    setPagination((prev) => ({...prev, pageIndex: 0}));
  }, [formValues]);

  const reset = () => {
    setPagination({pageIndex: 0, pageSize: initParams.pageSize});
    setSorting([]);
    setParams({...initParams});
    form?.reset();
  };

  useEffect(() => {
    if (sorting.length > 0) {
      const sortParams = {
        order: sorting[0].id,
        orderDir: sorting[0].desc ? 'desc' : 'asc',
        pageIndex: 0,
      };
      updatePageParams(sortParams);
    }
  }, [sorting]);

  useEffect(() => {
    updatePageParams(pagination);
  }, [pagination]);

  const updatePageParams = (params: any) => {
    setParams((prev: any) =>
      cleanPageParams({...prev, ...params}, defPageSize),
    );
  };

  const updateParams = (params: any) => {
    params = cleanParams(params);
    if (isEmpty(params)) {
      return;
    }
    let hasUpdate = false;
    Object.keys(params).forEach((key) => {
      const value = params[key];
      if (params[key]) {
        if (params[key] === value) {
          return;
        }
      }
      hasUpdate = true;
    });
    if (hasUpdate) {
      updatePageParams(params);
    }
  };

  return {
    pagination,
    setPagination,
    params,
    sorting,
    setSorting,
    reset,
    updateParams,
    form,
  };
}

function getInitSorting(queryParams: any) {
  const sorting: SortingState = [];
  if (queryParams.order) {
    sorting.push({
      id: queryParams.order,
      desc: queryParams.orderDir === 'desc',
    });
  }
  return sorting;
}

export function useUrlPagination(opts?: PaginationOptions): IPagination {
  const defPageSize = opts?.defPageSize || 20;
  const {params, setParams} = useSearchParams();
  const initParams = useMemo(
    () => cleanParams({pageSize: opts?.pageSize || 20, ...opts?.params}),
    [],
  );
  const [sorting, setSorting] = useState<SortingState>(getInitSorting(params));
  const isReset = useRef(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: params.pageIndex || (0 as number),
    pageSize: params.pageSize || (initParams.pageSize as number),
  });
  const form = useMemo(() => opts?.form, [opts?.form]);

  const formInit = useRef(false);
  useEffect(() => {
    if (form) {
      setTimeout(() => {
        const defValues = form.formState?.defaultValues;
        Object.keys(params).forEach((key) => {
          if (defValues && key in defValues) {
            form.setValue(key, params[key]);
          }
        });
        formInit.current = true;
      }, 1);
    }
  }, []);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const formValues = form ? useWatch({control: form.control}) : undefined;
  useEffect(() => {
    if (!formInit.current || !formValues) return;
    updatePageParams(formValues);
    setPagination((prev) => ({...prev, pageIndex: 0}));
  }, [formValues]);

  const updatePageParams = (params: any) => {
    setParams((prev: any) =>
      cleanPageParams({...prev, ...params}, defPageSize),
    );
  };

  useEffect(() => {
    if (isReset.current) {
      return;
    }
    if (sorting.length > 0) {
      const sortParams = {
        order: sorting[0].id,
        orderDir: sorting[0].desc ? 'desc' : 'asc',
      };
      updatePageParams(sortParams);
    } else {
      updatePageParams({order: '', orderDir: ''});
    }
  }, [sorting]);

  useEffect(() => {
    if (isReset.current) {
      return;
    }
    updatePageParams(pagination);
  }, [pagination]);

  const updateParams = (newParams: any) => {
    newParams = cleanParams(newParams);
    if (isEmpty(newParams)) {
      return;
    }
    let hasUpdate = false;
    Object.keys(newParams).forEach((key) => {
      const value = newParams[key];
      if (newParams[key]) {
        if (newParams[key] === value) {
          return;
        }
      }
      hasUpdate = true;
    });
    if (hasUpdate) {
      updatePageParams(newParams);
    }
  };

  const reset = () => {
    isReset.current = true;
    setTimeout(() => {
      setPagination({pageIndex: 0, pageSize: initParams.pageSize});
      setSorting([]);
      const _reset = {
        ...initParams,
        pageIndex: undefined,
        pageSize: initParams.pageSize,
        order: undefined,
        orderDir: undefined,
      };
      updatePageParams(_reset);
    }, 1);
    setTimeout(() => {
      form?.reset();
      isReset.current = false;
    }, 100);
  };
  useEffect(() => {
    if (isEmpty(params)) {
      reset();
    }
  }, [params]);

  const paramsOut = cleanParams({...initParams, ...params});
  return {
    pagination,
    setPagination,
    params: paramsOut,
    sorting,
    setSorting,
    reset,
    updateParams,
    form,
  };
}
